import React from 'react';
import styled, { keyframes, ThemeProvider } from 'styled-components';
import { theme } from '../../../styles/theme';
import { Helmet } from 'react-helmet-async';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpa,
  faWater,
  faPlaceOfWorship,
  faLeaf,
  faTree,
  faCircleNotch,
  faClock,
  faTicketAlt,
  faShoePrints,
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';
import NewsletterSignUp from '../../common/Newsletter-sign';
import { getCanonicalUrl } from '../../../utils/seo';

const IMG_HERO = 'https://BelvoirCastle.b-cdn.net/Wellness/Wellness%203%20(1).png';
const IMG_WOODLAND = 'https://BelvoirCastle.b-cdn.net/Wellness/Wellness%202.png';
const IMG_BALLROOM = 'https://BelvoirCastle.b-cdn.net/Wellness/Wellness%205.png';
const IMG_CHAPEL = 'https://BelvoirCastle.b-cdn.net/Wellness/Chapel%20(1).jpg';
const IMG_STONE_CIRCLE = 'https://BelvoirCastle.b-cdn.net/Wellness/Wellness%204.png';
const IMG_LOGO = 'https://BelvoirCastle.b-cdn.net/Wellness/Screenshot%202026-09-04%20at%2013.42.03.png';

const WELLNESS_BOOKING =
  '/booking-journey?locationId=978&pageId=b90fafa0-440f-4c69-831e-1f7e434cb8c8';

const TREE_TRAIL_BOOKING =
  '/booking-journey?locationId=978&pageId=37b0d9d6-3ed8-420b-bf5f-04419a7b5eb6';

const C = {
  salmon: '#B29C8D',
  plaster: '#D7B7A7',
  sage: '#BDB59C',
  accent: '#302D2C',
  ivory: '#F7F3EE',
  forest: '#302D2C',
  deep: '#302D2C',
  gold: '#B29C8D',
  goldSoft: '#D7B7A7',
  rose: '#B29C8D',
  cream: '#EFE6DC',
  parchment: '#D7B7A7',
  ink: '#302D2C',
  mute: '#6F635C',
};

const wellnessTheme = {
  ...theme,
  colors: {
    ...theme.colors,
    primary: C.accent,
    secondary: C.accent,
    accent: C.salmon,
    offWhite: C.ivory,
    greyBackground: C.sage,
  },
};

const fadeUp = keyframes`
  from { opacity: 0; transform: translateY(20px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Page = styled.div`
  min-height: 100vh;
  background: ${C.ivory};
  padding-top: 158px;
  color: ${C.ink};

  @media (max-width: 768px) {
    padding-top: clamp(188px, 34vw, 228px);
  }
`;

const Hero = styled.section`
  position: relative;
  min-height: clamp(560px, 86vh, 860px);
  display: flex;
  align-items: flex-end;
  overflow: hidden;
  color: #fff;
`;

const HeroImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
  object-position: center 30%;
`;

const HeroOverlay = styled.div`
  position: absolute;
  inset: 0;
  background:
    linear-gradient(
      to top,
      rgba(48, 45, 44, 0.94) 0%,
      rgba(48, 45, 44, 0.72) 28%,
      rgba(48, 45, 44, 0.22) 58%,
      rgba(48, 45, 44, 0.08) 100%
    );
`;

const HeroInner = styled.div`
  position: relative;
  z-index: 1;
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  padding: clamp(2.5rem, 6vw, 4.5rem) clamp(1.25rem, 5vw, 3rem) clamp(3rem, 7vw, 5rem);
  animation: ${fadeUp} 0.8s ease both;
`;

const Lockup = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.55rem;
  margin-bottom: 1.5rem;
`;

const BrandWord = styled.p`
  margin: 0;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(2.6rem, 7vw, 5.2rem);
  font-weight: 500;
  letter-spacing: 0.22em;
  line-height: 0.95;
  text-transform: uppercase;
`;

const BrandRule = styled.span`
  display: block;
  width: 4.5rem;
  height: 1px;
  background: ${C.gold};
`;

const BrandSub = styled.p`
  margin: 0;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: clamp(0.72rem, 1.4vw, 0.82rem);
  font-weight: 600;
  letter-spacing: 0.42em;
  text-transform: uppercase;
  color: ${C.goldSoft};
`;

const HeroTitle = styled.h1`
  margin: 0 0 0.85rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(2.1rem, 5vw, 3.6rem);
  font-weight: 500;
  font-style: italic;
  line-height: 1.1;
`;

const HeroMeta = styled.p`
  margin: 0 0 1.75rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: clamp(0.92rem, 1.8vw, 1.05rem);
  letter-spacing: 0.04em;
  color: rgba(255, 255, 255, 0.82);
`;

const HeroActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
`;

const Btn = styled.a`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 3.1rem;
  padding: 0.9rem 1.7rem;
  border-radius: 2px;
  text-decoration: none;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  transition: background 0.2s ease, color 0.2s ease, border-color 0.2s ease, transform 0.18s ease;

  &:hover {
    transform: translateY(-1px);
  }
`;

const BtnGold = styled(Btn)`
  background: ${C.plaster};
  color: ${C.accent};
  border: 1px solid ${C.plaster};

  &:hover {
    background: ${C.salmon};
    border-color: ${C.salmon};
    color: #fff;
  }
`;

const BtnGhost = styled(Btn)`
  background: transparent;
  color: #fff;
  border: 1px solid rgba(255, 255, 255, 0.45);

  &:hover {
    border-color: #fff;
    background: rgba(255, 255, 255, 0.06);
  }
`;

const BtnForest = styled(Btn)`
  background: ${C.forest};
  color: #fff;
  border: 1px solid ${C.forest};

  &:hover {
    background: ${C.salmon};
    border-color: ${C.salmon};
  }
`;

const BtnOutline = styled(Btn)`
  background: transparent;
  color: ${C.forest};
  border: 1px solid ${C.forest};

  &:hover {
    background: ${C.salmon};
    border-color: ${C.salmon};
    color: #fff;
  }
`;

const Section = styled.section`
  padding: clamp(3.5rem, 7vw, 5.75rem) clamp(1.25rem, 5vw, 3rem);
  background: ${p => p.$bg || C.ivory};
`;

const Inner = styled.div`
  max-width: 1100px;
  margin: 0 auto;
  animation: ${fadeUp} 0.65s ease both;
`;

const Kicker = styled.p`
  margin: 0 0 0.75rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: ${p => (p.$onDark ? C.plaster : C.accent)};
`;

const Divider = styled.div`
  width: 48px;
  height: 2px;
  background: ${p => (p.$onDark ? C.plaster : C.salmon)};
  margin-bottom: 1.25rem;
`;

const SectionTitle = styled.h2`
  margin: 0 0 1.25rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(2rem, 4.5vw, 3.1rem);
  font-weight: 500;
  line-height: 1.15;
  color: ${C.accent};

  em {
    font-style: italic;
    color: ${C.accent};
    font-weight: 400;
  }
`;

const Lead = styled.p`
  margin: 0 0 1.15rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.2rem, 2.2vw, 1.45rem);
  line-height: 1.65;
  color: ${C.deep};
  max-width: 40rem;
`;

const Body = styled.p`
  margin: 0 0 1rem;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 1.02rem;
  line-height: 1.75;
  color: ${C.ink};
  max-width: 42rem;
  opacity: 0.88;

  &:last-child {
    margin-bottom: 0;
  }
`;

const TwoCol = styled.div`
  display: grid;
  grid-template-columns: ${p => p.$cols || '1.05fr 0.95fr'};
  gap: clamp(2rem, 5vw, 3.5rem);
  align-items: ${p => p.$align || 'center'};

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const PhotoFrame = styled.div`
  position: relative;
  overflow: hidden;
  min-height: clamp(280px, 42vw, 480px);
  background: ${C.accent};
  box-shadow: 0 0 0 1px ${C.sage};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    min-height: clamp(280px, 42vw, 480px);
  }
`;

const ExperienceGrid = styled.ul`
  list-style: none;
  margin: 2.25rem 0 0;
  padding: 0;
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;

  @media (max-width: 900px) {
    grid-template-columns: 1fr 1fr;
  }

  @media (max-width: 620px) {
    grid-template-columns: 1fr;
  }
`;

const ExperienceCard = styled.li`
  padding: 0;
  background: ${C.ivory};
  border: 1px solid ${C.sage};
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ExperienceImage = styled.div`
  height: 160px;
  overflow: hidden;
  background: ${C.accent};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
`;

const ExperienceBody = styled.div`
  padding: 1.4rem 1.3rem 1.5rem;
`;

const ExperienceIcon = styled.div`
  width: 2.4rem;
  height: 2.4rem;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${C.sage};
  color: ${C.accent};
  margin-bottom: 0.9rem;
  font-size: 0.95rem;
`;

const ExperienceTitle = styled.h3`
  margin: 0 0 0.45rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.35rem;
  font-weight: 600;
  color: ${C.forest};
`;

const ExperienceText = styled.p`
  margin: 0;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.94rem;
  line-height: 1.6;
  color: ${C.mute};
`;

const TrailPanel = styled.div`
  display: grid;
  grid-template-columns: 1.15fr 0.85fr;
  background: ${C.forest};
  color: #fff;
  overflow: hidden;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const TrailCopy = styled.div`
  padding: clamp(2.5rem, 5vw, 3.75rem);
`;

const TrailMeta = styled.div`
  padding: clamp(2.5rem, 5vw, 3.75rem);
  background: linear-gradient(160deg, ${C.accent} 0%, #241f1e 100%);
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1.15rem;
  border-left: 1px solid rgba(255, 255, 255, 0.08);

  @media (max-width: 860px) {
    border-left: none;
    border-top: 1px solid rgba(255, 255, 255, 0.08);
  }
`;

const WhiteTitle = styled(SectionTitle)`
  color: #fff;

  em {
    color: ${C.goldSoft};
  }
`;

const WhiteBody = styled(Body)`
  color: rgba(255, 255, 255, 0.82);
  opacity: 1;
`;

const MetaRow = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  padding-bottom: 0.9rem;
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);

  &:last-of-type {
    border-bottom: none;
    padding-bottom: 0;
  }
`;

const MetaLabel = styled.span`
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${C.goldSoft};
`;

const MetaValue = styled.span`
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.25rem;
  font-weight: 600;
  text-align: right;
`;

const QuoteBlock = styled.blockquote`
  margin: 0;
  padding: 0;
  border: none;
`;

const Quote = styled.p`
  margin: 0 0 1.25rem;
  font-family: ${p => p.theme.fonts.main};
  font-size: clamp(1.55rem, 3.2vw, 2.15rem);
  font-style: italic;
  font-weight: 500;
  line-height: 1.4;
  color: ${C.forest};
`;

const QuoteAttr = styled.cite`
  display: block;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.72rem;
  font-style: normal;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: ${C.accent};
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
`;

const InfoItem = styled.div`
  display: grid;
  grid-template-columns: minmax(140px, 200px) 1fr;
  gap: 1.5rem;
  padding: 1.4rem 0;
  border-bottom: 1px solid ${C.sage};

  &:first-child {
    padding-top: 0;
  }

  &:last-child {
    border-bottom: none;
    padding-bottom: 0;
  }

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
    gap: 0.4rem;
  }
`;

const InfoLabel = styled.h3`
  margin: 0;
  font-family: ${p => p.theme.fonts.main};
  font-size: 1.25rem;
  font-weight: 600;
  color: ${C.forest};
`;

const InfoText = styled.p`
  margin: 0;
  font-family: system-ui, -apple-system, sans-serif;
  font-size: 0.98rem;
  line-height: 1.7;
  color: ${C.ink};
  opacity: 0.85;

  a {
    color: ${C.accent};
    font-weight: 600;
    text-decoration: underline;
    text-underline-offset: 2px;
  }
`;

const BrandBand = styled.section`
  position: relative;
  min-height: clamp(420px, 62vh, 640px);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  color: #fff;
`;

const BrandImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const BrandScrim = styled.div`
  position: absolute;
  inset: 0;
  background: rgba(48, 45, 44, 0.28);
`;

const CtaBlock = styled.div`
  text-align: center;
  max-width: 560px;
  margin: 0 auto;
`;

const Notice = styled.aside`
  margin-top: 2.25rem;
  padding: 1.35rem 1.5rem;
  background: ${C.ivory};
  border: 1px solid ${C.plaster};
  border-left: 3px solid ${C.salmon};
  color: ${C.ink};

  p {
    margin: 0;
    font-family: system-ui, -apple-system, sans-serif;
    font-size: 0.95rem;
    line-height: 1.65;
  }

  p + p {
    margin-top: 0.7rem;
  }

  em {
    font-style: italic;
    color: ${C.mute};
  }
`;

const EXPERIENCES = [
  {
    icon: faSpa,
    title: 'Yoga in the Elizabeth Saloon',
    text: 'Drop in, stretch, and settle. A first-come, first-served class inside one of the Castle’s most beautiful rooms.',
  },
  {
    icon: faWater,
    title: 'Sound Baths in the Ballroom',
    text: 'Let the tension go beneath the vaulted ceiling — sound, stillness, and a rare hour of quiet grandeur.',
  },
  {
    icon: faPlaceOfWorship,
    title: 'Chapel Services',
    text: 'Contemplations, meditations, and conversation in the Chapel — a space to pause and listen.',
    image: IMG_CHAPEL,
    imageAlt: 'The Chapel at Belvoir Castle',
  },
  {
    icon: faLeaf,
    title: 'Mind Body Belvoir Studio',
    text: 'Talks, drop-in sessions, and classes in the Engine Yard — a first look at Mind Body Belvoir Studio, part of Belvoir Wellness.',
  },
  {
    icon: faCircleNotch,
    title: 'Stone Circle tours',
    text: 'Guided walks to the Stone Circle, meeting the landscape, history, and spirit of Belvoir outdoors.',
    image: IMG_STONE_CIRCLE,
    imageAlt: 'The Belvoir Stone Circle overlooking the Vale',
  },
  {
    icon: faTree,
    title: 'Sensory Tree Trail Tours',
    text: 'A slow, grounding walk among some of the oldest living things on the estate. Booking essential.',
  },
];

const BelvoirWellness = () => {
  const canonical = getCanonicalUrl('whats-on/belvoir-wellness');

  return (
    <Page>
      <Helmet>
        <title>Belvoir Wellness — A Day of Discovery | Belvoir Castle</title>
        <link rel="canonical" href={canonical} />
        <meta
          name="description"
          content="Sunday 20 September, 10am–5pm. Her Grace invites you to Belvoir Castle for Mind, Body, Belvoir — a first opportunity to discover wellness experiences to come. Castle entry at regular admission; wellness activities free."
        />
        <meta property="og:title" content="Belvoir Wellness — A Day of Discovery" />
        <meta
          property="og:description"
          content="A regal open day of movement, stillness, nature, sound, and conversation. Sunday 20 September at Belvoir Castle."
        />
        <meta property="og:image" content={IMG_HERO} />
        <meta property="og:url" content={canonical} />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <script type="application/ld+json">
          {JSON.stringify({
            '@context': 'https://schema.org',
            '@type': 'Event',
            name: 'Belvoir Wellness — A Day of Discovery',
            description:
              'Her Grace warmly invites you to Belvoir Castle for Mind, Body, Belvoir — a first opportunity to discover wellness experiences to come. Yoga, sound baths, Chapel Services, Mind Body Belvoir Studio, Stone Circle tours, and Sensory Tree Trail Tours.',
            image: [IMG_HERO, IMG_WOODLAND, IMG_BALLROOM, IMG_CHAPEL, IMG_STONE_CIRCLE, IMG_LOGO],
            url: canonical,
            startDate: '2026-09-20T10:00:00+01:00',
            endDate: '2026-09-20T17:00:00+01:00',
            eventStatus: 'https://schema.org/EventScheduled',
            eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
            location: {
              '@type': 'Place',
              name: 'Belvoir Castle',
              address: {
                '@type': 'PostalAddress',
                streetAddress: 'Belvoir Castle',
                addressLocality: 'Grantham',
                postalCode: 'NG32 1PE',
                addressRegion: 'Leicestershire',
                addressCountry: 'GB',
              },
            },
            organizer: {
              '@type': 'Organization',
              name: 'Belvoir Castle',
              url: 'https://www.belvoircastle.com',
            },
            offers: {
              '@type': 'Offer',
              url: `https://www.belvoircastle.com${WELLNESS_BOOKING}`,
              priceCurrency: 'GBP',
              availability: 'https://schema.org/InStock',
              description:
                'Castle events are ticketed at regular admission. All other wellness activities are free of charge. Sensory Tree Trail booking is essential.',
            },
          })}
        </script>
      </Helmet>

      <Hero>
        <HeroImage src={IMG_HERO} alt="A guest in meditation at Belvoir — Mind, Body, Belvoir" />
        <HeroOverlay />
        <HeroInner>
          <Lockup>
            <BrandWord>Belvoir</BrandWord>
            <BrandRule />
            <BrandSub>Wellness</BrandSub>
          </Lockup>
          <HeroTitle>A Day of Discovery</HeroTitle>
          <HeroMeta>Sunday 20 September · 10am–5pm · Belvoir Castle</HeroMeta>
          <HeroActions>
            <BtnGold href={WELLNESS_BOOKING}>Book now</BtnGold>
            <BtnGhost href="#the-day">Explore the day</BtnGhost>
          </HeroActions>
        </HeroInner>
      </Hero>

      <Section id="the-day" $bg={C.ivory}>
        <Inner>
          <TwoCol $align="start">
            <div>
              <Kicker>An invitation from Her Grace</Kicker>
              <Divider />
              <SectionTitle>
                Mind, Body, <em>Belvoir</em>
              </SectionTitle>
              <Lead>
                Her Grace warmly invites you to Belvoir Castle for a special day of Mind, Body,
                Belvoir — a first opportunity to discover our wellness experiences to come.
              </Lead>
              <Body>
                Come and spend the day with us as we mark the start of something new: a wellness
                open day, right across the Castle and grounds, and the first taste of what is to
                come.
              </Body>
              <Body>
                Explore the landscape, history, and spirit of Belvoir through a day of movement,
                stillness, nature, sound, and conversation. Come for one activity or stay for it
                all, and leave feeling rested, rejuvenated, and part of the beginning of Belvoir’s
                wellness journey.
              </Body>
              <div style={{ marginTop: '1.75rem' }}>
                <BtnForest href={WELLNESS_BOOKING}>Book now</BtnForest>
              </div>
            </div>
            <QuoteBlock>
              <Quote>
                Come as you are. Move, pause, explore, listen, connect — and discover the beginnings
                of Belvoir Wellness.
              </Quote>
              <QuoteAttr>Sunday 20 September</QuoteAttr>
            </QuoteBlock>
          </TwoCol>
        </Inner>
      </Section>

      <Section $bg={C.sage}>
        <Inner>
          <Kicker>The day</Kicker>
          <Divider />
          <SectionTitle>
            A first look at <em>what is to come</em>
          </SectionTitle>
          <Lead>
            Discover Mind Body Belvoir Studio — part of Belvoir Wellness — through talks, drop-in
            sessions, and classes, and join guided tours of the Stone Circle and our Sensory Tree
            Trail Tours.
          </Lead>
          <Body>
            Inside the Castle (Castle entry ticket add-on required) — enjoy yoga in the Elizabeth
            Saloon, sound baths in the Ballroom, and contemplations, meditations, and conversations
            in the Chapel.
          </Body>
          <ExperienceGrid>
            {EXPERIENCES.map(item => (
              <ExperienceCard key={item.title}>
                {item.image && (
                  <ExperienceImage>
                    <img src={item.image} alt={item.imageAlt || item.title} />
                  </ExperienceImage>
                )}
                <ExperienceBody>
                  {!item.image && (
                    <ExperienceIcon>
                      <FontAwesomeIcon icon={item.icon} />
                    </ExperienceIcon>
                  )}
                  <ExperienceTitle>{item.title}</ExperienceTitle>
                  <ExperienceText>{item.text}</ExperienceText>
                </ExperienceBody>
              </ExperienceCard>
            ))}
          </ExperienceGrid>
          <Notice>
            <p>
              We are delighted to offer a complimentary goodie bag and an exclusive studio discount
              card to our first 50 visitors to The Mind. Body. Belvoir Wellness Studio in the Engine
              Yard.
            </p>
            <p>
              <em>
                Register your interest at the studio on the day to receive your gift and stay
                connected.
              </em>
            </p>
          </Notice>
        </Inner>
      </Section>

      <Section $bg={C.ivory} style={{ paddingBottom: 0 }}>
        <Inner>
          <TwoCol $cols="0.9fr 1.1fr">
            <div>
              <Kicker>On the same day</Kicker>
              <Divider />
              <SectionTitle>
                Sensory Tree Trail <em>Tours</em>
              </SectionTitle>
              <Body>
                Wander through ancient woodland on a guided Sensory Tree Trail Tour, taking you to
                the tallest yew tree in Europe — possibly overtaken by now, but still a giant — the
                storied Parliament Oak, monkey puzzle trees planted over 200 years ago, and more.
              </Body>
              <Body>
                It is a slow, grounding walk among some of the oldest living things on the estate —
                the perfect way to reconnect before the day continues.
              </Body>
            </div>
            <PhotoFrame>
              <img
                src={IMG_WOODLAND}
                alt="Ancient woodland on the Belvoir estate, home of the Tree Trail"
              />
            </PhotoFrame>
          </TwoCol>
        </Inner>
      </Section>

      <Section $bg={C.ivory} id="tree-trail">
        <Inner>
          <TrailPanel>
            <TrailCopy>
              <Kicker $onDark>Sunday 20 September</Kicker>
              <WhiteTitle>
                Book your place on the <em>Sensory Tree Trail</em>
              </WhiteTitle>
              <WhiteBody>
                Two tours on the wellness open day. Spaces are limited and booking is essential.
                Sensible footwear is recommended. Please be aware that due to steps, inclines and
                uneven terrain, wheelchair and buggy access is not possible.
              </WhiteBody>
              <div style={{ marginTop: '1.75rem' }}>
                <BtnGold href={TREE_TRAIL_BOOKING}>Book now</BtnGold>
              </div>
            </TrailCopy>
            <TrailMeta>
              <MetaRow>
                <MetaLabel>Morning</MetaLabel>
                <MetaValue>11am</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Afternoon</MetaLabel>
                <MetaValue>2pm</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Duration</MetaLabel>
                <MetaValue>Approx. 1 hour 30 mins</MetaValue>
              </MetaRow>
              <MetaRow>
                <MetaLabel>Price</MetaLabel>
                <MetaValue>Free</MetaValue>
              </MetaRow>
            </TrailMeta>
          </TrailPanel>
        </Inner>
      </Section>

      <Section $bg={C.cream}>
        <Inner>
          <TwoCol $cols="1.1fr 0.9fr">
            <PhotoFrame>
              <img
                src={IMG_BALLROOM}
                alt="The Ballroom at Belvoir Castle, home of sound baths on the wellness day"
              />
            </PhotoFrame>
            <div>
              <Kicker>Inside the Castle</Kicker>
              <Divider />
              <SectionTitle>
                Rooms made for <em>stillness</em>
              </SectionTitle>
              <Body>
                Yoga in the Elizabeth Saloon. Sound baths in the Ballroom. Contemplations,
                meditations, and conversations in the Chapel. Castle entry ticket add-on required,
                offered as the rooms allow, first come, first served.
              </Body>
              <Body>
                Castle events are ticketed at the regular admission price. All other wellness
                activities are free of charge.
              </Body>
              <div style={{ marginTop: '1.75rem' }}>
                <BtnOutline href={WELLNESS_BOOKING}>Book Castle tickets</BtnOutline>
              </div>
            </div>
          </TwoCol>
        </Inner>
      </Section>

      <Section $bg={C.ivory}>
        <Inner>
          <Kicker>A very special hour</Kicker>
          <Divider />
          <SectionTitle>
            A Q&amp;A with <em>Her Grace</em>
          </SectionTitle>
          <Lead>
            The day includes a very special Q&amp;A with Her Grace, where she will share her
            personal wellness and spiritual journey, what wellbeing means to her, and her vision
            for this new chapter of wellness at Belvoir.
          </Lead>
          <Body>
            This is the beginning of Belvoir Wellness — not a finished programme, but an invitation
            to be among the first to feel what this new chapter might become.
          </Body>
        </Inner>
      </Section>

      <BrandBand aria-label="Belvoir Wellness">
        <BrandImage src={IMG_LOGO} alt="Belvoir Wellness crest over the estate woodland" />
        <BrandScrim />
      </BrandBand>

      <Section $bg="#fff">
        <Inner>
          <Kicker>Practical information</Kicker>
          <Divider />
          <SectionTitle>
            Plan your <em>day</em>
          </SectionTitle>
          <InfoList>
            <InfoItem>
              <InfoLabel>Date &amp; time</InfoLabel>
              <InfoText>Sunday 20 September · 10am–5pm</InfoText>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Tickets</InfoLabel>
              <InfoText>
                Castle events are ticketed at the regular admission price.{' '}
                <a href={WELLNESS_BOOKING}>Book now</a>.
              </InfoText>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Wellness sessions</InfoLabel>
              <InfoText>
                Yoga, sound baths, Chapel Services, talks at Mind Body Belvoir Studio, and tours
                are free of charge. Castle sessions are first come, first served.
              </InfoText>
            </InfoItem>
            <InfoItem>
              <InfoLabel>Sensory Tree Trail Tours</InfoLabel>
              <InfoText>
                Sunday 20 September, 11am and 2pm. Approximately 1 hour 30 minutes. Free, booking
                essential.{' '}
                <a href={TREE_TRAIL_BOOKING}>Book a Sensory Tree Trail Tour</a>. Sensible footwear
                recommended.
              </InfoText>
            </InfoItem>
            <InfoItem>
              <InfoLabel>What to wear</InfoLabel>
              <InfoText>
                Come as you are. Comfortable clothing for movement and stillness, and layers for a
                day that moves between Castle rooms and the estate grounds.
              </InfoText>
            </InfoItem>
            <InfoItem>
              <InfoLabel>
                <FontAwesomeIcon icon={faShoePrints} style={{ marginRight: '0.4rem' }} />
                Grounds
              </InfoLabel>
              <InfoText>
                The Sensory Tree Trail Tours and Stone Circle walks travel woodland paths. Wear
                shoes you are happy to walk in for an hour and a half. Please be aware that due
                to steps, inclines and uneven terrain, wheelchair and buggy access is not possible.
              </InfoText>
            </InfoItem>
          </InfoList>
        </Inner>
      </Section>

      <ThemeProvider theme={wellnessTheme}>
        <NewsletterSignUp
          tag="belvoir-wellness"
          customTitle="Stay close to Belvoir Wellness"
          customDescription="Be first to hear as this new chapter unfolds — classes, retreats, and the next invitations from Her Grace."
        />
      </ThemeProvider>

      <Section $bg={C.cream}>
        <Inner>
          <CtaBlock>
            <Kicker>Join us</Kicker>
            <Divider style={{ margin: '0 auto 1.25rem' }} />
            <SectionTitle style={{ textAlign: 'center' }}>
              Discover the beginnings of <em>Belvoir Wellness</em>
            </SectionTitle>
            <Body style={{ margin: '0 auto 1.75rem', textAlign: 'center' }}>
              Come for one activity or stay for it all. Book Castle entry for the day, reserve your
              Sensory Tree Trail place, and take a first step into what is to come.
            </Body>
            <HeroActions style={{ justifyContent: 'center' }}>
              <BtnForest href={WELLNESS_BOOKING}>
                <FontAwesomeIcon icon={faTicketAlt} style={{ marginRight: '0.55rem' }} />
                Book now
              </BtnForest>
              <BtnOutline href={TREE_TRAIL_BOOKING}>
                <FontAwesomeIcon icon={faClock} style={{ marginRight: '0.55rem' }} />
                Book Sensory Tree Trail
              </BtnOutline>
            </HeroActions>
          </CtaBlock>
        </Inner>
      </Section>

      <Footer />
    </Page>
  );
};

export default BelvoirWellness;
